import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.js';
import User from 'lucide-react/dist/esm/icons/user.js';
import Settings from 'lucide-react/dist/esm/icons/settings.js';
import LogOut from 'lucide-react/dist/esm/icons/log-out.js';

import { useT } from '../i18n/strings.js';
import { ROUTES } from '../lib/nav.js';

export interface AccountMenuProps {
  name: string;
  role: string;
  loggingOut: boolean;
  onLogout: () => void;
}

/**
 * ux-review defect (chrome pass): the signed-in user's name was inert plain text, and
 * "Log out" sat immediately beside it as a bare one-click text link — logging out
 * mid-file loses the officer's place (the exact "dumped at a login screen with the work
 * lost" failure this codebase already names for session expiry, I24). Putting logout
 * behind a menu adds a deliberate extra step before the destructive action fires,
 * and gives "Account settings" (I23) a real entry point instead of no entry point at
 * all.
 *
 * Built the same way `Tooltip.tsx` is — a small custom absolutely-positioned panel,
 * not `@radix-ui/react-dropdown-menu` (not a dependency here, and Radix's
 * Popper-based overlays are the documented reason Combobox/Select/Tooltip in
 * `@ministry/ui` avoid the floating-ui positioning stack in this repo's jsdom test
 * environment — see those components' own comments). `role="menu"`/`"menuitem"`,
 * `Escape` closes and returns focus to the trigger, and a click outside the panel
 * closes it — the three behaviors DESIGN_SYSTEM.md §7.4 requires of any overlay.
 */
export function AccountMenu({ name, role, loggingOut, onLogout }: AccountMenuProps): React.JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

  const close = React.useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) close(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close(true);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    // Move focus onto the first item the moment the menu opens, so keyboard users
    // land inside it rather than needing an extra Tab press.
    itemRefs.current[0]?.focus();
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  const moveFocus = (fromIndex: number, delta: number) => {
    const count = itemRefs.current.length;
    const nextIndex = (fromIndex + delta + count) % count;
    itemRefs.current[nextIndex]?.focus();
  };

  const items = [
    {
      id: 'settings' as const,
      label: t('shell.account.settings'),
      icon: <Settings aria-hidden="true" width={16} height={16} />,
      onSelect: () => {
        close(true);
        navigate(ROUTES.account);
      },
    },
    {
      id: 'logout' as const,
      label: loggingOut ? t('shell.loggingOut') : t('shell.account.logout'),
      icon: <LogOut aria-hidden="true" width={16} height={16} />,
      onSelect: () => {
        close(false);
        onLogout();
      },
    },
  ];

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('shell.account.menuLabel', { name })}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex min-h-touch items-center gap-2 rounded-md px-2 text-sm font-semibold text-text-primary transition-colors duration-fast hover:bg-surface-hover focus-visible:outline-none focus-visible:shadow-focus"
      >
        <User aria-hidden="true" width={18} height={18} />
        <span className="hidden max-w-[10rem] truncate sm:inline">{t('shell.account.roleSummary', { name, role })}</span>
        <ChevronDown aria-hidden="true" width={14} height={14} />
      </button>

      {open ? (
        <div
          role="menu"
          aria-label={t('shell.account.menuLabel', { name })}
          className="absolute right-0 top-full z-50 mt-1 min-w-[12rem] rounded-md border border-border-decorative bg-surface-elevated py-1 shadow-elevation-2"
        >
          {items.map((item, index) => (
            <button
              key={item.id}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              type="button"
              role="menuitem"
              disabled={item.id === 'logout' && loggingOut}
              onClick={item.onSelect}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  moveFocus(index, 1);
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  moveFocus(index, -1);
                }
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary hover:bg-surface-hover focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:text-text-disabled"
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
